import BarChartByVehicleInteractionType from '@customer/components/charts/by-vehicle-interaction/BarChartByVehicleInteractionType';
import ChartScenarios from '@customer/components/charts/chart-scenarios/ChartScenarios';
import { ChartColorScheme, ScenarioConfig } from '@customer/components/charts/config';
import AnalyticsTable from '@customer/components/tables/analytics-table/AnalyticsTable';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import {
  colDefActiveDuration,
  columnDefVIRemoteObjectName,
} from '@customer/components/tables/grid/columnDefs';
import VehicleInteractionTypeRenderer from '@customer/components/tables/cells/vehicle-interaction-type/VehicleInteractionTypeRenderer';
import {
  AssetListScenarios,
  AssetVIFilters,
  useGetAssetInteractionsSummaryQuery,
  useListAssetVehicleInteractionsQuery,
} from '@customer/store/apis/assetsApi';
import { VIListModel } from '@customer/store/apis/interactionsApi';
import { AssetVIScenarioQueryParams, StatsScenario } from '@customer/store/apis/scenariosApi';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import {
  openSidePanel,
  SidePanel,
  usePerformanceTimeRangeFilters,
  VehicleInteractionSidePanelTab,
} from '@customer/store/slices/globalSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { ColDef, ValueGetterParams } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import FilterButton from '@customer/components/inputs/filter-button/FilterButton';
import FilterSummary from '@customer/components/inputs/filter-summary/FilterSummary';
import SubTabsPageContent from '@customer/templates/sub-tabs-page/SubTabsPageContent';
import HistogramChartScenario from '@customer/components/charts/histogram-chart-scenario/HistogramChartScenario';
import AreaChartScenario from '@customer/components/charts/area-chart-scenario/AreaChartScenario';
import { FilterGroup } from '@customer/filters/types';
import useFilters from '@customer/filters/useFilters';
import InsightsCardVI from '@customer/components/data-display/insights-card-vi/InsightsCardVI';
import useLocationFilterController from '@customer/components/maps/location-filter/useLocationFilterController';
import { useGetVIHeatmapQuery } from '@customer/store/apis/heatmapsApi';
import LocationFilter from '@customer/components/maps/location-filter/LocationFilter';
import { useLocationFilterParams } from '@customer/store/slices/locationFilterSlice';
import PerformanceTimeRange from '@customer/components/inputs/performance-time-range/PerformanceTimeRange';
import BarChartByAssetGroup from '@customer/components/charts/by-asset-group/BarChartByAssetGroup';
import BarChartByAsset from '@customer/components/charts/by-asset/BarChartByAsset';
import {
  useDistanceFormatter,
  useSpeedFormatter,
} from '@customer/components/tables/formatters/numberFormatters';
import {
  GoodInvalidFormatter,
  YesNoFormatter,
} from '@customer/components/tables/formatters/booleanFormatters';
import { useAbsoluteTimePartialColDef } from '@customer/components/tables/cells/absolute-time/useAbsoluteTime';
import { getObjectType } from '@cas/utils/translationHelpers';
import { ObjectTypeId } from '@dmc/ui';
import AssetNameRenderer from '@customer/components/tables/cells/asset-name/AssetNameRenderer';
import DotNetixNameTooltip from '@customer/components/tables/tooltips/dotnetix-name-tooltip/DotNetixNameTooltip';

const AssetInteractionsGridSliceKey = 'AssetInteractions';
const filterGroup = FilterGroup.AssetVI;
type Model = VIListModel;

export function SingleAssetVehicleInteractions() {
  const { assetId } = useParams<string>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const siteId = useAppSelector(state => state.auth.siteId);

  const timeRangeFilters = usePerformanceTimeRangeFilters();

  const filters = useFilters<AssetVIFilters>(filterGroup);

  const locationFilters = useLocationFilterParams({
    filterGroup,
    includeAssetId: true,
    assetId,
  });

  const getRowId = (data: Model) => data.id;

  const speedFormatter = useSpeedFormatter();
  const distanceFormatter = useDistanceFormatter();
  const absoluteTimeColDef = useAbsoluteTimePartialColDef<Model>();

  const columnDefs = useMemo<ColDef<Model>[]>(
    () => [
      {
        field: 'ts',
        headerName: t('Date and Time'),
        sort: 'desc',
        minWidth: 180,
        ...absoluteTimeColDef,
      },
      {
        field: 'roNa',
        headerName: t('ROName'),
        cellRenderer: AssetNameRenderer,
        tooltipField: 'roDN',
        tooltipComponent: DotNetixNameTooltip,
        minWidth: 180,
        ...columnDefVIRemoteObjectName(t),
      },
      {
        field: 'it',
        headerName: t('InteractionType'),
        cellRenderer: VehicleInteractionTypeRenderer,
        minWidth: 180,
      },
      { ...colDefActiveDuration(t('Duration')), minWidth: 110 },
      {
        field: 'dist',
        headerName: t('Distance'),
        valueFormatter: distanceFormatter<Model, 'dist'>,
        minWidth: 110,
      },
      {
        field: 'loSpd',
        headerName: t('objectSpeed.LO'),
        valueFormatter: speedFormatter<Model, 'loSpd'>,
        minWidth: 120,
      },
      {
        field: 'roSpd',
        headerName: t('objectSpeed.RO'),
        valueFormatter: speedFormatter<Model, 'roSpd'>,
        minWidth: 120,
      },
      {
        field: 'aud',
        headerName: t('Audible'),
        valueFormatter: YesNoFormatter<Model, 'aud'>,
        cellRenderer: null,
        minWidth: 90,
      },
      {
        field: 'svfix',
        headerName: t('GpsFix'),
        valueFormatter: GoodInvalidFormatter<Model, 'svfix'>,
        cellRenderer: null,
        minWidth: 110,
      },
      {
        field: 'roObj',
        headerName: t('ROObjectType'),
        valueGetter: ({ data }: ValueGetterParams<Model>): string =>
          getObjectType(t, data?.roObj ?? ObjectTypeId.Unknown),
        minWidth: 150,
      },
      { field: 'rn', headerName: t('Rule'), minWidth: 150 },
    ],
    [t, speedFormatter, distanceFormatter, absoluteTimeColDef],
  );

  const isSkipQuery = typeof assetId === 'undefined' || typeof siteId === 'undefined';

  const { data: interactionsData, isFetching } = useListAssetVehicleInteractionsQuery(
    isSkipQuery
      ? skipToken
      : {
          assetId,
          query: {
            ...filters,
            ...locationFilters,
            ...timeRangeFilters,
            scenario: AssetListScenarios.PerformanceVehicleInteractions,
          },
        },
    { refetchOnMountOrArgChange: 10 },
  );

  const { currentData: interactionsSummaryData } = useGetAssetInteractionsSummaryQuery(
    isSkipQuery
      ? skipToken
      : {
          assetId,
          query: {
            ...filters,
            ...locationFilters,
            ...timeRangeFilters,
            scenario: AssetListScenarios.PerformanceVehicleInteractions,
          },
        },
    { refetchOnMountOrArgChange: 60 },
  );

  const onRowClick = useCallback(
    (data: Model) => {
      dispatch(
        openSidePanel({
          sidePanel: SidePanel.VehicleInteraction,
          tab: VehicleInteractionSidePanelTab.Summary,
          modelId: data.id.toString(),
        }),
      );
    },
    [dispatch],
  );

  const getDataSetLabel = useCallback(
    (label: string) => {
      return t(`vi.level.${label}`);
    },
    [t],
  );

  const scenarioConfigs = useMemo<ScenarioConfig[]>(
    () => [
      {
        title: t('ByTimeRange'),
        ChartComponent: AreaChartScenario<AssetVIScenarioQueryParams>,
        scenario: StatsScenario.VehicleInteractionsByTimeRange,
        hideResultsLimited: true,
        enableCustomTimeRange: true,
      },
      {
        title: t('ByTimeOfDay'),
        ChartComponent: HistogramChartScenario<AssetVIScenarioQueryParams>,
        scenario: StatsScenario.VehicleInteractionsByTimeOfDay,
        hideResultsLimited: true,
      },
      {
        title: t('ByROAssetGroup'),
        ChartComponent: BarChartByAssetGroup<AssetVIScenarioQueryParams>,
        scenario: StatsScenario.VehicleInteractionsByRemoteObjectAssetGroup,
      },
      {
        title: t('ByROAsset'),
        ChartComponent: BarChartByAsset<AssetVIScenarioQueryParams>,
        scenario: StatsScenario.VehicleInteractionsByRemoteObject,
      },
      {
        title: t('ByInteractionType'),
        ChartComponent: BarChartByVehicleInteractionType<AssetVIScenarioQueryParams>,
        scenario: StatsScenario.VehicleInteractionsByInteractionType,
        hideResultsLimited: true,
      },
    ],
    [t],
  );

  const locationFilterProps = useLocationFilterController({
    filterGroup,
    useHeatmapQuery: useGetVIHeatmapQuery,
    includeAssetId: true,
    assetId,
  });

  return (
    <SubTabsPageContent
      headerSummary={
        <FilterSummary
          transKey="ShowingInteractions"
          filteredTransKey="ShowingFilteredInteractions"
          countAll={interactionsData?.countAll}
          countFilter={interactionsData?.countFilter}
          filterGroup={filterGroup}
        />
      }
      headerActions={
        <>
          <PerformanceTimeRange disabled={isFetching} />
          <FilterButton filters={filterGroup} disabled={isFetching} />
        </>
      }
      body={
        <>
          <InsightsCardVI summaries={interactionsSummaryData?.vi} />

          <ChartScenarios
            title={t('VehicleInteractions')}
            scenarioConfigs={scenarioConfigs}
            filterGroup={filterGroup}
            colorScheme={ChartColorScheme.RedOrangeYellow}
            getDataSetLabel={getDataSetLabel}
            tooltipTransKey="InteractionsWithCount"
            includeAssetId
            assetId={assetId}
            includeTimeRange
          />

          <LocationFilter {...locationFilterProps} />

          <AnalyticsTable
            title={t('VehicleInteractions')}
            hasData={typeof interactionsData !== 'undefined'}
            noDataDescription={t('NoVehicleInteractions')}
          >
            <GridControlled<Model>
              sliceKey={AssetInteractionsGridSliceKey}
              rowData={interactionsData?.items ?? []}
              columnDefs={columnDefs}
              suppressHorizontalScroll={false}
              getRowId={getRowId}
              onRowClick={onRowClick}
              noRowsMessage={t('NoVehicleInteractions')}
            />
          </AnalyticsTable>
        </>
      }
    />
  );
}

export default SingleAssetVehicleInteractions;
